import React, { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import RolesContext from '../../../../contexts/RolesContext';
import { AppDivisionContext } from '../../../../contexts/AppDivisionContext';
import { assetStatusesApi } from '../../../../utils/api/asset';
import {
  ProjectAsset,
  projectAssetsApi,
} from '../../../../utils/api/projectAsset';
import ConfirmationModal from '../../../Shared/ConfirmationModal/ConfirmationModal';
import Loading from '../../../Shared/ResourceIndex/Loading';
import AssetCard from '../../../AssetTypes/AssetCard/AssetCard';
import AssetBulkUpload from './AssetBulkUpload';
import CreateAssetModal from './CreateAssetModal';

interface IProjectAssetsProps {
  projectId: number;
}

type ArchiveFilter = 'active' | 'archived';

const ProjectAssets: React.FC<IProjectAssetsProps> = ({ projectId }) => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const { appDivisionId } = useContext(AppDivisionContext);
  const queryClient = useQueryClient();
  const canEdit = permCheck('update', 'Project');

  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState<number | 'all'>('all');
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('active');
  const [showModal, setShowModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editAsset, setEditAsset] = useState<ProjectAsset | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ProjectAsset | null>(null);

  const assetsKey = ['projectAssets', projectId];

  const assetsQuery = useQuery(assetsKey, () =>
    projectAssetsApi.index(projectId),
  );
  const divisionId = appDivisionId || 0;
  const statusesQuery = useQuery(['assetStatuses', divisionId], () =>
    assetStatusesApi.index(divisionId),
  );

  const invalidate = () => queryClient.invalidateQueries(assetsKey);

  const archiveMutation = useMutation(
    (id: number) => projectAssetsApi.archive(projectId, id),
    {
      onSuccess: () => {
        toast.success('Asset archived');
        invalidate();
      },
      onError: () => {
        toast.error('Failed to archive asset.');
      },
    },
  );

  const restoreMutation = useMutation(
    (id: number) => projectAssetsApi.restore(projectId, id),
    {
      onSuccess: () => {
        toast.success('Asset restored');
        invalidate();
      },
      onError: () => {
        toast.error('Failed to restore asset.');
      },
    },
  );

  const statuses = statusesQuery.data || [];

  const filtered = useMemo(() => {
    const assets = assetsQuery.data || [];
    const term = search.trim().toLowerCase();

    return assets.filter((a) => {
      const matchesArchive =
        archiveFilter === 'archived' ? !!a.archived_at : !a.archived_at;
      const matchesStatus =
        statusId === 'all' || a.asset_status_id === statusId;
      const matchesSearch =
        !term ||
        a.asset_name.toLowerCase().includes(term) ||
        (a.asset_type?.name || '').toLowerCase().includes(term);

      return matchesArchive && matchesStatus && matchesSearch;
    });
  }, [assetsQuery.data, search, statusId, archiveFilter]);

  const hasAnyAssets = (assetsQuery.data || []).length > 0;

  return (
    <div className='project-assets'>
      <div className='project-assets__toolbar'>
        <input
          className='project-assets__search'
          type='search'
          placeholder='Search assets…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className='project-assets__filter'
          value={statusId}
          onChange={(e) =>
            setStatusId(
              e.target.value === 'all' ? 'all' : Number(e.target.value),
            )
          }
        >
          <option value='all'>All statuses</option>
          {statuses.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <div className='project-assets__archive-toggle'>
          <button
            type='button'
            className={archiveFilter === 'active' ? 'is-active' : ''}
            onClick={() => setArchiveFilter('active')}
          >
            Active
          </button>
          <button
            type='button'
            className={archiveFilter === 'archived' ? 'is-active' : ''}
            onClick={() => setArchiveFilter('archived')}
          >
            Archived
          </button>
        </div>

        <span className='spacer' />

        {canEdit && (
          <button
            className='outline'
            onClick={() => setShowBulkUpload(true)}
          >
            Add Bulk Asset
          </button>
        )}

        {canEdit && (
          <button
            className='primary project-assets__create'
            onClick={() => {
              setEditAsset(null);
              setShowModal(true);
            }}
          >
            + Create Asset
          </button>
        )}
      </div>

      {assetsQuery.isLoading ? (
        <Loading />
      ) : filtered.length === 0 ? (
        <p className='project-assets__empty'>
          {!hasAnyAssets
            ? 'No assets yet for this project.'
            : 'No assets match the current filters.'}
        </p>
      ) : (
        filtered.map((asset) => (
          <AssetCard
            key={asset.id}
            projectId={projectId}
            asset={asset}
            canEdit={canEdit}
            onEdit={(a) => {
              setEditAsset(a);
              setShowModal(true);
            }}
            onArchive={(a) => setArchiveTarget(a)}
            onRestore={(a) => restoreMutation.mutate(a.id)}
            onChanged={invalidate}
          />
        ))
      )}

      {showBulkUpload && (
        <AssetBulkUpload
          projectId={projectId}
          divisionId={divisionId}
          handleClose={() => setShowBulkUpload(false)}
          onSaved={invalidate}
        />
      )}

      {showModal && (
        <CreateAssetModal
          projectId={projectId}
          divisionId={divisionId}
          asset={editAsset}
          onSaved={invalidate}
          onClose={() => {
            setEditAsset(null);
            setShowModal(false);
          }}
        />
      )}

      <ConfirmationModal
        show={!!archiveTarget}
        title={`Archive "${archiveTarget?.asset_name}"?`}
        subTitle='Archived assets are hidden from inspections but keep their history. You can restore them anytime.'
        buttonText='Archive Asset'
        handleConfirm={() => {
          if (archiveTarget) archiveMutation.mutate(archiveTarget.id);
          setArchiveTarget(null);
        }}
        handleClose={() => setArchiveTarget(null)}
      />
    </div>
  );
};

export default ProjectAssets;
